import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  alpha
} from '@mui/material'
import VerifiedIcon from '@mui/icons-material/Verified'
import ScienceIcon from '@mui/icons-material/Science'
import SchoolIcon from '@mui/icons-material/School'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BusinessIcon from '@mui/icons-material/Business'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LockIcon from '@mui/icons-material/Lock'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PublicIcon from '@mui/icons-material/Public'
import FactoryIcon from '@mui/icons-material/Factory'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import aboutStats from '../data/aboutStats.json'

// Animated counter component
function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '', decimals = 0 }) {
  const [count, setCount] = useState(0)
  const countRef = useRef(null)
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true)
          let startTime = null
          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp
            const progress = Math.min((timestamp - startTime) / duration, 1)
            const easeOutQuart = 1 - Math.pow(1 - progress, 4)
            setCount(easeOutQuart * end)
            if (progress < 1) {
              requestAnimationFrame(animate)
            }
          }
          requestAnimationFrame(animate)
        }
      },
      { threshold: 0.3 }
    )

    if (countRef.current) {
      observer.observe(countRef.current)
    }

    return () => observer.disconnect()
  }, [end, duration, hasAnimated])

  const formatNumber = (num) => {
    if (decimals > 0) {
      return num.toFixed(decimals)
    }
    return Math.round(num).toLocaleString()
  }

  return (
    <span ref={countRef}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  )
}

// Stat card component for the "By The Numbers" section
function BigStatCard({ value, label, sublabel, icon: Icon, color = 'primary' }) {
  const theme = useTheme()
  return (
    <Card
      sx={{
        height: '100%',
        textAlign: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.2)}`
        }
      }}
    >
      <CardContent sx={{ py: 3 }}>
        {Icon && (
          <Icon sx={{ fontSize: 32, color: `${color}.main`, mb: 1 }} />
        )}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: `${color}.main`,
            mb: 0.5,
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          {value}
        </Typography>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {sublabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// Validation badge component
function ValidationBadge({ icon: Icon, title, subtitle, count }) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ color: 'white', fontSize: 24 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {count && <>{count} </>}{title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Box>
  )
}

function About() {
  const theme = useTheme()
  const navigate = useNavigate()

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 8,
          mb: 6,
          mx: -3,
          mt: -3,
          px: 3
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontFamily: 'Outfit, sans-serif',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            {aboutStats.hero.headline}
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, opacity: 0.9, maxWidth: 800, fontWeight: 400 }}
          >
            {aboutStats.hero.subheadline}
          </Typography>

          {/* Quick stats banner */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 2, sm: 3 }
            }}
          >
            {aboutStats.hero.quickStats.map((stat, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  flex: { xs: '0 0 45%', sm: '0 0 30%', md: '0 0 18%' }
                }}
              >
                <Typography variant="h3" fontWeight={700} sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Section 1: What Is This Dashboard? */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            What Is This Dashboard?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', maxWidth: 900 }}>
            This interactive visualization hub provides complete transparency into 15+ years of PermaFrost NMR
            treatment data. Every chart, every number, every case study is backed by PE-certified field
            measurements - not marketing estimates or lab simulations.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {['Engineers & Facility Managers', 'Procurement Teams', 'Energy Consultants', 'Researchers'].map((audience) => (
              <Grid item xs={6} sm={3} key={audience}>
                <Chip
                  label={audience}
                  sx={{
                    width: '100%',
                    py: 2,
                    fontSize: '0.9rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.1)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section 2: What Makes This Dataset Unique - Comparison Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            What Makes This Dataset Unique?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Most HVAC efficiency claims rely on lab tests or marketing materials. This corpus is different.
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Standard HVAC Data</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.main' }}>This Corpus</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aboutStats.comparisonTable.rows.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                        {row.standard}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <strong>{row.thisCorpus}</strong>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Section 3: How HVAC Products Are Typically Tested */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            How HVAC Products Are Typically Tested
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Understanding the industry standard puts this corpus in perspective.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, mb: 3 }}>
            {aboutStats.industryContrast?.industryBenchmarks?.map((b, i) => (
              <Card key={i} variant="outlined" sx={{ flex: '1 1 220px', minWidth: 220 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{b.standard}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{b.testing}</Typography>
                  <Typography variant="caption" color="error.main">Field validation: {b.fieldValidation}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card sx={{ mt: 3, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="success.main" gutterBottom>
                This Corpus
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                <li><Typography variant="body2">Sample size: <strong>254 PE-certified measurements</strong></Typography></li>
                <li><Typography variant="body2">Verification: <strong>100%</strong> independently validated</Typography></li>
                <li><Typography variant="body2">Testing environment: <strong>83 real-world sites</strong></Typography></li>
                <li><Typography variant="body2">Field validation: <strong>15+ years of data</strong></Typography></li>
                <li><Typography variant="body2">PE certification: <strong>113 PE-certified studies</strong></Typography></li>
                <li><Typography variant="body2">Lab validation: <strong>6 independent labs</strong></Typography></li>
              </Box>
            </CardContent>
          </Card>

          {/* PermaFrost NMR Independent Validation Table */}
          <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
            PermaFrost NMR Independent Validation
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Validation Source</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sample Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Key Finding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aboutStats.industryContrast?.nmrValidation?.map((study, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: study.type.includes('Field') ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.05),
                      '& td': { fontWeight: study.type.includes('Field') ? 700 : 400 }
                    }}
                  >
                    <TableCell>
                      <Chip label={study.type} size="small" color={study.type === 'Lab + Field' ? 'success' : study.type === 'Field' ? 'primary' : 'default'} variant={study.type.includes('Field') ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>{study.source}</TableCell>
                    <TableCell>{study.sampleSize}</TableCell>
                    <TableCell>{study.finding}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Section 4: Validation Depth */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            Validation Depth
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Independent validation from multiple sources - not just internal testing.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={ScienceIcon}
                title="Independent Labs"
                subtitle="NSF, UL, FAU, China National Lab, OEM Labs"
                count={6}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={VerifiedIcon}
                title="PE-Certified Studies"
                subtitle="Legally binding PE certifications"
                count={113}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={SchoolIcon}
                title="Academic Studies"
                subtitle="FAU 2013, UMontana 2021 & 2022"
                count={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={AccountBalanceIcon}
                title="US Government Facilities"
                subtitle="Air Force, DoD, National Guard"
                count={5}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={BusinessIcon}
                title="Fortune 500 Companies"
                subtitle="Real deployments, not pilots"
                count={31}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Section 5: By The Numbers */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            By The Numbers
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={134} />}
                label="Case Studies"
                icon={VerifiedIcon}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={254} />}
                label="PE-Certified Measurements"
                icon={VerifiedIcon}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={4.6} decimals={1} suffix="M" />}
                label="Data Points"
                icon={TrendingUpIcon}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={83} />}
                label="Locations Worldwide"
                icon={PublicIcon}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={24} />}
                label="US States"
                icon={PublicIcon}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={6} />}
                label="Countries"
                icon={PublicIcon}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={23.2} decimals={1} suffix="%" />}
                label="Avg Energy Savings"
                icon={TrendingUpIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <BigStatCard
                value={<AnimatedCounter end={26} />}
                label="Industries"
                icon={FactoryIcon}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Energy & Cost Savings with note */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="success.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    <AnimatedCounter end={12.3} decimals={1} suffix="M kWh" />
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>Energy Saved</Typography>
                  <Typography variant="caption" color="text.secondary">
                    From tested units only, not portfolio rollouts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="success.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    <AnimatedCounter end={1.9} decimals={1} prefix="$" suffix="M" />
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>Cost Savings Documented</Typography>
                  <Typography variant="caption" color="text.secondary">
                    From tested units only, not portfolio rollouts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>300,000+</strong> systems treated globally (portfolio deployments beyond documented corpus studies)
            </Typography>
          </Box>
        </Box>

        {/* Section 6: ML Validation Highlight */}
        <Box sx={{ mb: 8 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              border: `2px solid ${theme.palette.primary.main}`
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Machine Learning Validation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    University of Montana, 2022
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    241,029
                  </Typography>
                  <Typography variant="body2">Data points analyzed</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    9
                  </Typography>
                  <Typography variant="body2">ML algorithms tested</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    R² = 0.998
                  </Typography>
                  <Typography variant="body2">Post-treatment accuracy</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    R² = 0.76-0.95
                  </Typography>
                  <Typography variant="body2">Pre-treatment (erratic)</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                "{aboutStats.mlValidation.keyInsight}"
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Section 7: Radical Transparency */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            Radical Transparency
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We don't hide the lower performers. Real data includes real variance.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: alpha(theme.palette.warning.main, 0.05), border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
                <CardContent>
                  <WarningAmberIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {aboutStats.transparency.lowerPerformers.count} Studies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    showed &lt;15% improvement ({aboutStats.transparency.lowerPerformers.percentage}% of corpus)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {aboutStats.transparency.fullDistribution.min}% - {aboutStats.transparency.fullDistribution.max}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Full improvement range shown - not cherry-picked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <VerifiedIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {aboutStats.transparency.excludedMeasurements.count} Excluded
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For methodological reasons - all documented
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Known Limitations (Documented):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {aboutStats.transparency.limitations.map((limitation, index) => (
                <li key={index}>
                  <Typography variant="body2" color="text.secondary">{limitation}</Typography>
                </li>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Section 7.5: Data Anonymization */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <LockIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: 'Outfit, sans-serif' }}>
              Data Anonymization
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph>
            This repository has been intentionally anonymized to protect customer identities.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom color="primary.main">
                    Obfuscated Names (Customer Privacy)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    All customer/client names use a standardized placeholder system:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Major Telecom A', 'Major Global Bank A', 'Retail Chain B', 'QSR Chain A', 'Hotel Chain A', 'Defense Contractor A'].map(name => (
                      <Chip key={name} label={name} size="small" variant="outlined" />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>32 unique companies</strong> across 130+ case studies, all anonymized
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom color="success.main">
                    Real Names Present
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The following are NOT anonymized (public entities or technology provider):
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Product Company:</strong> Powertron Global LLC</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Testing Labs:</strong> FAU, University of Montana, UL, NSF International, Texas Oiltech Labs</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Manufacturers:</strong> Carrier, Trane, York, Lennox, Daikin, etc.</Typography>
                    <Typography variant="body2"><strong>Geographic:</strong> City/state names remain for context (without company ID)</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Section 8: Why This Data Matters for Scale-Up */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            Why This Data Matters for Scale-Up
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Planning a large rollout? This dataset provides the foundation for confident decision-making.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {aboutStats.scaleUpValue.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <RocketLaunchIcon sx={{ fontSize: 28, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section 9: Financial Impact */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            Financial Impact
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h3" fontWeight={700} color="primary.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    ${aboutStats.financialImpact.avgSavingsPerSystem.toLocaleString()}
                  </Typography>
                  <Typography variant="body1">Average savings per system</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h3" fontWeight={700} color="success.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    {aboutStats.financialImpact.paybackPeriod.min}-{aboutStats.financialImpact.paybackPeriod.max}
                  </Typography>
                  <Typography variant="body1">Month payback period</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h3" fontWeight={700} color="secondary.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    {aboutStats.financialImpact.costComparison.multiplier}
                  </Typography>
                  <Typography variant="body1">Less expensive than replacement</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${aboutStats.financialImpact.costComparison.permafrost.perTon}/ton vs ${aboutStats.financialImpact.costComparison.replacement.perTon.min}-{aboutStats.financialImpact.costComparison.replacement.perTon.max}/ton
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Footer CTA */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Ready to Explore the Data?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Dive into the case studies, calculate your ROI, or explore the raw data.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="inherit"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/case-studies')}
              sx={{ color: 'primary.main', bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
            >
              Explore Case Studies
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => navigate('/roi-calculator')}
              sx={{ borderColor: 'white', '&:hover': { bgcolor: alpha('#fff', 0.1) } }}
            >
              Try ROI Calculator
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => navigate('/data-explorer')}
              sx={{ borderColor: 'white', '&:hover': { bgcolor: alpha('#fff', 0.1) } }}
            >
              View Raw Data
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default About
